import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';

import { HappyUsers } from '@/components/landing/HappyUsers';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

const MEDIA = 'https://media.easy-peasy.ai/landing/image';
const HOME = 'https://media.easy-peasy.ai/landing/home';

// Each slide pairs a generated image with the EXACT prompt that made it —
// the hero cycles them together so visitors watch prompts become pictures.
const SLIDES = [
  {
    src: `${MEDIA}/hero-whale.webp`,
    prompt:
      'Surreal fine-art photograph, a colossal translucent glass whale drifting above a mirror-still salt flat at sunset, tiny human silhouette below, pastel sky reflected everywhere, dreamlike atmosphere, ultra detailed',
    short:
      'A colossal glass whale drifting above a mirror-still salt flat at sunset…',
  },
  {
    src: `${MEDIA}/hero-samurai.webp`,
    prompt:
      'Cinematic wide shot, lone samurai in flowing crimson robes standing in a field of silver pampas grass under a dramatic storm sky, hair and fabric caught in the wind, moody rim light, film still, epic composition',
    short: 'Lone samurai in crimson robes in a field of silver pampas grass…',
  },
  {
    src: `${MEDIA}/hero-atelier.webp`,
    prompt:
      'Wide editorial photograph of a bright artist atelier flooded with god rays, ribbons of vivid liquid paint frozen mid-air swirling around a sculptor at work, dust particles glowing, dynamic composition, hyperreal detail',
    short: 'An artist atelier with ribbons of liquid paint frozen mid-air…',
  },
  {
    src: `${MEDIA}/hero-city.webp`,
    prompt:
      'Sweeping aerial vista of a futuristic bioluminescent city carved into ocean cliffs at dusk, glowing turquoise veins across the architecture, waterfalls dropping into the sea, cinematic scale, concept art masterpiece',
    short: 'A bioluminescent city carved into ocean cliffs at dusk…',
  },
  {
    src: `${HOME}/img-astronaut.webp`,
    prompt:
      'Lone astronaut walking through a field of bioluminescent flowers on an alien planet, twin moons in a violet sky, cinematic sci-fi concept art, volumetric fog, epic scale',
    short:
      'An astronaut in a field of bioluminescent flowers, twin moons above…',
  },
];

const HERO_MODEL = 'Seedream 5.0 Pro';
const SLIDE_MS = 5000;

type ImageModelChip = { name: string; viewBox?: string; path?: string };

const IMAGE_MODELS: ImageModelChip[] = [
  {
    name: 'GPT Image 2',
    viewBox: '0 0 24 24',
    path: 'M22.282 9.821a5.99 5.99 0 0 0-.516-4.91 6.05 6.05 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.99 5.99 0 0 0-3.998 2.9 6.05 6.05 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.05 6.05 0 0 0 6.515 2.9A5.99 5.99 0 0 0 13.26 24a6.06 6.06 0 0 0 5.772-4.206 6 6 0 0 0 3.997-2.9 6.06 6.06 0 0 0-.747-7.073zM13.26 22.43a4.48 4.48 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.8.8 0 0 0 .392-.681v-6.737l2.02 1.168a.07.07 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494M3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.77.77 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646M2.34 7.896a4.5 4.5 0 0 1 2.366-1.973V11.6a.77.77 0 0 0 .388.677l5.815 3.354-2.02 1.168a.08.08 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855-5.833-3.387L15.119 7.2a.08.08 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023-.141-.085-4.774-2.782a.78.78 0 0 0-.785 0L9.409 9.23V6.897a.07.07 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08-4.778 2.758a.8.8 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5Z',
  },
  {
    name: 'Nano Banana Pro',
    viewBox: '0 0 340 340',
    path: 'M0 170C0 76.3 76.3 0 170 0c37.8 0 73.7 12.2 103.7 35.2l-39.5 51.3c-18.6-14.3-40.8-21.8-64.2-21.8-58.1 0-105.3 47.2-105.3 105.3S111.9 275.3 170 275.3c46.8 0 86.4-30.7 100.2-72.9H170v-64.7h170V170c0 93.7-76.3 170-170 170S0 263.7 0 170',
  },
  {
    name: 'Seedream 5.0',
    viewBox: '0 0 32 32',
    path: 'M5.915 25.44 1 26.702V4.311l4.915 1.263zm23.75 1.31-4.924 1.264V3l4.924 1.254zm-15.952-.627-4.915 1.264v-13.19l4.915 1.263zm3.21-13.883 4.925-1.264v13.19l-4.924-1.264z',
  },
  {
    name: 'FLUX.2',
    viewBox: '0 0 490 345',
    path: 'M349.391 147.163h-52.114l-52.113-73.418L82.594 302.52h52.227l110.341-155.352h52.113L186.935 302.52h52.369l110.087-155.355v73.417l-58.013 81.949v42.597h-30.154l-.001.002h-52.114l.001-.002h-52.439l-.004.006h-52.113l.004-.006H0L245.164 0zM490 345.13h-52.114l-.001-.002h-30.156v-42.44l-58.338-82.106v-73.417z',
  },
  {
    name: 'Ideogram v4',
    viewBox: '0 0 12 12',
    path: 'M8.884.74a3.2 3.2 0 0 1 1.494 1.89 1.7 1.7 0 0 1 .627.398 1.7 1.7 0 0 1 .494 1.23 1.7 1.7 0 0 1-.546 1.207 1.69 1.69 0 0 1 .546 1.315 1.69 1.69 0 0 1-.654 1.264l-.018.015.01.016c.195.3.293.662.267 1.032l-.004.055a1.69 1.69 0 0 1-1.56 1.514 1.7 1.7 0 0 1-.77-.125 1.689 1.689 0 0 1-2.922.485 2 2 0 0 1-.818.199l-.062.004h-.449l-.034-.002c-.558-.033-.546-.887.034-.887h.402l.023-.002h.024a1.15 1.15 0 0 0 .039-2.3l-.04.002H1.404a.445.445 0 0 1 0-.89h3.517l.023-.001h.024a1.15 1.15 0 0 0 .254-2.273l-.07-.015a1 1 0 0 0-.15-.023l-.035.001H1.404a.445.445 0 1 1 0-.888h3.518l.023-.002h.023a1.15 1.15 0 1 0 .044-2.3l-.044.001h-.483c-.558-.034-.547-.888.034-.888h.4L4.968.77q.301.001.578.083l.015-.01A3.2 3.2 0 0 1 8.885.74',
  },
  { name: 'Imagen 4' },
  { name: 'Grok' },
  { name: 'Recraft v4.1' },
  { name: 'Midjourney V7' },
  { name: 'Qwen Image Max' },
];

export const ImageModelChipsMarquee = () => (
  <div className='relative w-full overflow-hidden'>
    <div className='flex w-max items-center gap-x-8 animate-scroll-logos motion-reduce:animate-none'>
      {[false, true].map((isDuplicate) => (
        <React.Fragment key={isDuplicate ? 'dup' : 'main'}>
          {IMAGE_MODELS.map((model) => (
            <div
              key={`${isDuplicate ? 'dup-' : ''}${model.name}`}
              className='flex shrink-0 items-center gap-2 pr-8'
              aria-hidden={isDuplicate || undefined}
            >
              {model.path && (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox={model.viewBox}
                  width={16}
                  height={16}
                  aria-hidden='true'
                  className='h-[16px] w-auto shrink-0 fill-white/50'
                >
                  <path d={model.path} />
                </svg>
              )}
              <span className='shrink-0 text-base font-normal text-white/50'>
                {model.name}
              </span>
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
    <div className='pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#050509] to-transparent' />
    <div className='pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#050509] to-transparent' />
  </div>
);

const ImageHero = () => {
  const { t } = useLocale('ai-image-generator');
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((i) => (i + 1) % SLIDES.length);
    }, SLIDE_MS);
    return () => clearInterval(interval);
  }, []);

  const slide = SLIDES[slideIndex];

  return (
    <section className='relative overflow-hidden bg-[#050509]'>
      {/* Crossfading full-bleed backgrounds */}
      {SLIDES.map((s, i) => (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={s.src}
          src={s.src}
          alt=''
          loading={i === 0 ? 'eager' : 'lazy'}
          decoding='async'
          aria-hidden={i !== slideIndex || undefined}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 motion-reduce:transition-none ${
            i === slideIndex ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}

      {/* Legibility overlays */}
      <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_60%_at_50%_45%,rgba(5,5,9,0.7),rgba(5,5,9,0.35)_65%,rgba(5,5,9,0.15))]' />
      <div className='pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#050509]/90 to-transparent' />
      <div className='pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-[#050509] to-transparent' />

      {/* Navigation */}
      <div className='absolute top-0 left-0 w-full z-30'>
        <LandingPageNavigation variant='light' forceDark />
      </div>

      {/* Content */}
      <div className='relative z-20 mx-auto flex min-h-[92svh] max-w-4xl flex-col items-center justify-center px-4 pt-32 pb-28 text-center sm:px-6'>
        <div className='mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-200 backdrop-blur-md sm:text-sm'>
          <span className='rounded-full bg-indigo-500 px-2 py-0.5 text-[10px] font-bold text-white'>
            {t('NEW')}
          </span>
          {t('GPT Image 2 & Seedream 5.0 now live')}
        </div>

        <h1 className='text-5xl font-extrabold tracking-tight text-white drop-shadow-2xl sm:text-6xl md:text-7xl lg:text-[5rem]'>
          {t('Picture anything.')}
          <span className='mt-1 block bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text pb-2 text-transparent'>
            {t('In every style.')}
          </span>
        </h1>

        <p className='mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 drop-shadow-lg md:text-xl'>
          {t(
            '40+ top image models in one studio — GPT Image 2, Nano Banana Pro, Seedream 5.0, FLUX.2 and more. Photoreal to anime, up to 4K.',
          )}
        </p>

        {/* Live prompt bar — shows the real prompt behind the background image */}
        <Link
          href={`/ai-images?prompt=${encodeURIComponent(
            slide.prompt,
          )}&model=${encodeURIComponent(HERO_MODEL)}`}
          className='group mt-10 flex w-full max-w-2xl items-center gap-3 rounded-full border border-white/20 bg-black/40 py-3 pl-6 pr-3 text-left backdrop-blur-xl transition-colors hover:border-indigo-400/60'
        >
          <span
            key={slide.src}
            className='hero-prompt-text min-w-0 flex-1 truncate text-sm text-slate-200 sm:text-base'
          >
            {slide.short}
          </span>
          <span className='inline-flex shrink-0 items-center gap-2 rounded-full bg-indigo-500 px-5 py-2.5 text-sm font-bold text-white transition-colors group-hover:bg-indigo-400'>
            {t('Generate')}
            <span aria-hidden='true'>→</span>
          </span>
        </Link>
        <div className='mt-3 text-xs font-medium uppercase tracking-widest text-slate-400'>
          {t('The real prompt behind this image — click to remix it')}
        </div>

        <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
          <Link
            href='/ai-images'
            className='group inline-flex h-[54px] min-w-[220px] items-center justify-center gap-2 rounded-full bg-white px-8 text-lg font-bold text-slate-900 shadow-xl transition-all hover:bg-slate-100'
          >
            {t('Start creating free')}
            <span
              aria-hidden='true'
              className='transition-transform group-hover:translate-x-1'
            >
              →
            </span>
          </Link>
          <a
            href='#showcase'
            className='inline-flex h-[54px] min-w-[170px] items-center justify-center rounded-full border border-white/25 bg-black/25 px-8 text-lg font-semibold text-white backdrop-blur-md transition-all hover:border-white/45 hover:bg-black/40'
          >
            {t('See the gallery')}
          </a>
        </div>

        <div className='mt-8 flex justify-center'>
          <HappyUsers fromLandingPage={true} className='items-center' />
        </div>
      </div>

      {/* Image model chips pinned to hero bottom */}
      <div className='relative z-20 pb-6'>
        <ImageModelChipsMarquee />
      </div>

      <style jsx global>{`
        @keyframes hero-prompt-in {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .hero-prompt-text {
          animation: hero-prompt-in 0.6s ease both;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-prompt-text {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
};

export default ImageHero;
